import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SHARED_MODULES } from '../../../modules/shared-imports';
import { TranslatePipe } from 'src/app/i18n/translate.pipe';
import { FeedbackRating, FeedbackService } from 'src/app/services/feedback/feedback.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';

type Step = 'rate' | 'details' | 'thanks';

interface RatingOption {
  value: FeedbackRating;
  emoji: string;
  labelKey: string;
}

/**
 * App-wide feedback host, mounted once in AppComponent for logged-in users:
 *  - the "Give Feedback" dialog, opened from the avatar menu / mobile side menu via
 *    FeedbackService.openDialog(), or from the prompt card below;
 *  - the optional Dashboard prompt card, only when a SUPER_ADMIN/ROOT switched it on in
 *    Application Settings (off by default — a regular user sees nothing new until then);
 *  - the native App Store / Play Store review request — also off by default, triggered by usage
 *    alone, and never in the same session as the prompt card (store rules forbid asking for an
 *    opinion first or sending only happy users to the store).
 */
@Component({
  selector: 'app-feedback-host',
  standalone: true,
  imports: [SHARED_MODULES, TranslatePipe],
  templateUrl: './feedback-host.component.html',
  styleUrls: ['./feedback-host.component.css'],
})
export class FeedbackHostComponent implements OnInit, OnDestroy {
  private readonly feedbackService = inject(FeedbackService);
  private readonly router = inject(Router);
  readonly networkStatusService = inject(NetworkStatusService);

  readonly ratings: RatingOption[] = [
    { value: 'SAD', emoji: '😞', labelKey: 'appFeedback.rate.sad' },
    { value: 'NEUTRAL', emoji: '😐', labelKey: 'appFeedback.rate.neutral' },
    { value: 'HAPPY', emoji: '🙂', labelKey: 'appFeedback.rate.happy' },
    { value: 'LOVE', emoji: '😍', labelKey: 'appFeedback.rate.love' },
  ];

  dialogVisible = false;
  promptVisible = false;
  step: Step = 'rate';
  rating: FeedbackRating | null = null;
  text = '';
  showName = false;
  showCompany = false;
  sending = false;
  error = false;

  private promptCheckedThisSession = false;
  private promptTimer: ReturnType<typeof setTimeout> | null = null;
  private subs = new Subscription();

  get isPositive(): boolean {
    return this.rating === 'HAPPY' || this.rating === 'LOVE';
  }

  get selectedEmoji(): string {
    return this.ratings.find(r => r.value === this.rating)?.emoji ?? '';
  }

  ngOnInit(): void {
    this.subs.add(this.feedbackService.openDialog$.subscribe(() => this.open()));

    this.subs.add(
      this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
        if (!this.promptCheckedThisSession && e.urlAfterRedirects.startsWith('/dashboard')) {
          this.promptCheckedThisSession = true;
          // Let the dashboard finish loading first — never interrupt the first paint.
          this.promptTimer = setTimeout(() => this.checkPrompts(), 3500);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.promptTimer) clearTimeout(this.promptTimer);
  }

  private checkPrompts(): void {
    if (!this.networkStatusService.isOnline) return;
    this.feedbackService.promptStatus().subscribe(status => {
      const storeNow = status.requestStoreReview && Capacitor.isNativePlatform();
      if (storeNow) {
        this.feedbackService.requestStoreReview();
        return; // never show our own card in the same session as the store popup
      }
      if (status.showFeedbackPrompt && !this.dialogVisible && this.router.url.startsWith('/dashboard')) {
        this.promptVisible = true;
      }
    });
  }

  // ─── Prompt card ───

  dismissPrompt(): void {
    this.promptVisible = false;
    this.feedbackService.markPromptDismissed().subscribe();
  }

  pickFromPrompt(value: FeedbackRating): void {
    this.dismissPrompt();
    this.open();
    this.pickRating(value);
  }

  // ─── Dialog ───

  open(): void {
    this.step = 'rate';
    this.rating = null;
    this.text = '';
    this.showName = false;
    this.showCompany = false;
    this.error = false;
    this.sending = false;
    this.dialogVisible = true;
  }

  close(): void {
    this.dialogVisible = false;
  }

  pickRating(value: FeedbackRating): void {
    this.rating = value;
    this.error = false;
    this.step = 'details';
  }

  back(): void {
    this.step = 'rate';
    this.error = false;
  }

  onShowNameChange(on: boolean): void {
    if (!on) this.showCompany = false;
  }

  send(): void {
    if (!this.rating || this.sending || !this.networkStatusService.isOnline) return;
    const positive = this.isPositive;
    const text = this.text.trim() || null;
    this.sending = true;
    this.error = false;
    this.feedbackService.submit({
      rating: this.rating,
      highlightText: positive ? text : null,
      issueText: positive ? null : text,
      showName: positive && this.showName,
      showCompany: positive && this.showName && this.showCompany,
    }).subscribe({
      next: () => {
        this.sending = false;
        this.step = 'thanks';
      },
      error: () => {
        this.sending = false;
        this.error = true;
      },
    });
  }
}
